import type { FirebaseError } from 'firebase/app';

/**
 * Semantic error keys for `newAttendanceEvent`, mapped from Firebase HttpsError
 * code suffix + message (exact / includes only where the backend contract allows).
 */
/** Must stay in sync with Services `requireAttendanceJurisdictionId.ts`. */
export const ATTENDANCE_JURISDICTION_ID_REQUIRED_MESSAGE =
  'A valid local identifier is required to assign this user to a work journey';

export const NEW_ATTENDANCE_EVENT_ERROR = {
  PERMISSION_DENIED: 'permissionDenied',
  INVALID_PAYLOAD: 'invalidPayload',
  ATTENDANCE_RECORD_NOT_FOUND: 'attendanceRecordNotFound',
  ATTENDANCE_RECORD_MISMATCH: 'attendanceRecordMismatch',
  MISSING_JURISDICTION_ID: 'missingJurisdictionId',
  GENERIC: 'generic',
} as const;

export type NewAttendanceEventErrorKey =
  (typeof NEW_ATTENDANCE_EVENT_ERROR)[keyof typeof NEW_ATTENDANCE_EVENT_ERROR];

const CODE_SUFFIX = {
  PERMISSION_DENIED: 'permission-denied',
  INVALID_ARGUMENT: 'invalid-argument',
  NOT_FOUND: 'not-found',
  FAILED_PRECONDITION: 'failed-precondition',
  INTERNAL: 'internal',
} as const;

const readFirebaseError = (error: unknown): { code: string; message: string } => {
  if (!error || typeof error !== 'object') {
    return { code: '', message: '' };
  }
  const maybe = error as Partial<FirebaseError> & { code?: string; message?: string };
  return {
    code: typeof maybe.code === 'string' ? maybe.code : '',
    message: typeof maybe.message === 'string' ? maybe.message : '',
  };
};

const codeEndsWith = (code: string, suffix: string): boolean =>
  code === suffix || code.endsWith(`/${suffix}`);

/**
 * Maps a Firebase callable error from `newAttendanceEvent` to a stable UI key.
 * Portal / SHIFT_CHANGES-only errors fall through to GENERIC for this mark flow.
 */
export const mapNewAttendanceEventError = (error: unknown): NewAttendanceEventErrorKey => {
  const { code, message } = readFirebaseError(error);

  if (codeEndsWith(code, CODE_SUFFIX.PERMISSION_DENIED) && message === 'User not authorized') {
    return NEW_ATTENDANCE_EVENT_ERROR.PERMISSION_DENIED;
  }

  if (
    codeEndsWith(code, CODE_SUFFIX.INVALID_ARGUMENT) &&
    message.includes('Missing required parameters')
  ) {
    return NEW_ATTENDANCE_EVENT_ERROR.INVALID_PAYLOAD;
  }

  if (
    codeEndsWith(code, CODE_SUFFIX.NOT_FOUND) &&
    message.includes('Attendance record')
  ) {
    return NEW_ATTENDANCE_EVENT_ERROR.ATTENDANCE_RECORD_NOT_FOUND;
  }

  if (
    codeEndsWith(code, CODE_SUFFIX.FAILED_PRECONDITION) &&
    message.includes('does not belong to the employee')
  ) {
    return NEW_ATTENDANCE_EVENT_ERROR.ATTENDANCE_RECORD_MISMATCH;
  }

  if (
    codeEndsWith(code, CODE_SUFFIX.FAILED_PRECONDITION) &&
    message.includes(ATTENDANCE_JURISDICTION_ID_REQUIRED_MESSAGE)
  ) {
    return NEW_ATTENDANCE_EVENT_ERROR.MISSING_JURISDICTION_ID;
  }

  if (codeEndsWith(code, CODE_SUFFIX.INTERNAL)) {
    return NEW_ATTENDANCE_EVENT_ERROR.GENERIC;
  }

  return NEW_ATTENDANCE_EVENT_ERROR.GENERIC;
};

export const NEW_ATTENDANCE_EVENT_ERROR_MESSAGE_ID: Record<NewAttendanceEventErrorKey, string> = {
  [NEW_ATTENDANCE_EVENT_ERROR.PERMISSION_DENIED]:
    'workJourney.realTime.markAttendance.error.permissionDenied',
  [NEW_ATTENDANCE_EVENT_ERROR.INVALID_PAYLOAD]:
    'workJourney.realTime.markAttendance.error.invalidPayload',
  [NEW_ATTENDANCE_EVENT_ERROR.ATTENDANCE_RECORD_NOT_FOUND]:
    'workJourney.realTime.markAttendance.error.attendanceRecordNotFound',
  [NEW_ATTENDANCE_EVENT_ERROR.ATTENDANCE_RECORD_MISMATCH]:
    'workJourney.realTime.markAttendance.error.attendanceRecordMismatch',
  [NEW_ATTENDANCE_EVENT_ERROR.MISSING_JURISDICTION_ID]:
    'workJourney.realTime.markAttendance.error.missingJurisdictionId',
  [NEW_ATTENDANCE_EVENT_ERROR.GENERIC]: 'workJourney.realTime.markAttendance.error.generic',
};
